import { Map } from "immutable";
import { z } from "zod";

import {
	type JoinState,
	type SemijoinState,
	dehydrateJoinState,
	dehydrateSemijoinState,
	hydrateJoinState,
	hydrateSemijoinState,
	rawJoinStateSchema,
	rawSemijoinStateSchema,
} from "../operatorsV2";
import {
	type LeftOuterJoinState,
	dehydrateLeftOuterJoinState,
	hydrateLeftOuterJoinState,
	rawLeftOuterJoinStateSchema,
} from "../operatorsV2/leftOuterJoin";
import {
	type OrderedTopKState,
	dehydrateOrderedTopKState,
	hydrateOrderedTopKState,
	rawOrderedTopKStateSchema,
} from "../operatorsV2/orderedTopK";

export const rawOperatorStateSchema = z.discriminatedUnion("type", [
	rawSemijoinStateSchema,
	rawJoinStateSchema,
	rawOrderedTopKStateSchema,
	rawLeftOuterJoinStateSchema,
]);
export type RawOperatorState = z.infer<typeof rawOperatorStateSchema>;
export type OperatorState<TLeft, TRight, TOut> =
	| SemijoinState<TLeft, TRight>
	| JoinState<TLeft, TRight>
	| OrderedTopKState<TLeft>
	| LeftOuterJoinState<TLeft, TRight, TOut>;

export const rawRunnableQueryStateSchema = z.record(z.string(), rawOperatorStateSchema);

export type RawRunnableQueryState = z.infer<typeof rawRunnableQueryStateSchema>;

export type RunnableQueryState = Map<
	string,
	| SemijoinState<unknown, unknown>
	| JoinState<unknown, unknown>
	| OrderedTopKState<unknown>
	| LeftOuterJoinState<unknown, unknown, unknown>
>;

export const hydrateRunnableQueryState = (stateJson: unknown): RunnableQueryState => {
	const rawState = rawRunnableQueryStateSchema.parse(stateJson);
	return Map(
		Object.entries(rawState).map(([queryId, rawState]) => {
			let hydratedState: OperatorState<unknown, unknown, unknown>;
			switch (rawState.type) {
				case "semijoin": {
					hydratedState = hydrateSemijoinState(rawState);
					break;
				}
				case "join": {
					hydratedState = hydrateJoinState(rawState);
					break;
				}
				case "orderedTopK": {
					hydratedState = hydrateOrderedTopKState(rawState);
					break;
				}
				case "leftOuterJoin": {
					hydratedState = hydrateLeftOuterJoinState(rawState);
					break;
				}
			}
			return [queryId, hydratedState];
		})
	);
};

export const dehydrateRunnableQueryState = (state: RunnableQueryState): RawRunnableQueryState => {
	const rawState = state.toArray().map(([queryId, state]): [string, RawOperatorState] => {
		let dehydratedState: RawOperatorState;
		switch (state.type) {
			case "semijoin": {
				dehydratedState = dehydrateSemijoinState(state);
				break;
			}
			case "join": {
				dehydratedState = dehydrateJoinState(state);
				break;
			}
			case "orderedTopK": {
				dehydratedState = dehydrateOrderedTopKState(state);
				break;
			}
			case "leftOuterJoin": {
				dehydratedState = dehydrateLeftOuterJoinState(state);
				break;
			}
		}
		return [queryId, dehydratedState];
	});
	return Object.fromEntries(rawState);
};
